import { useCallback, useEffect, useMemo, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "./supabase";
import { civilDate, localDateStr } from "./date";

// A range is loaded completely and replaces its previous snapshot. Merging by id
// alone would retain deleted/rescheduled visits after the operator makes a change.
const EMPTY_JOBS = [];
export function useScheduleJobs(from, to, initialJobs = EMPTY_JOBS) {
  const key = `${from}/${to}`;
  const [snapshot, setSnapshot] = useState(null);
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision(n => n + 1), []);
  const seed = useMemo(() => initialJobs.filter(j => j.scheduled_date >= from && j.scheduled_date <= to), [initialJobs, from, to]);

  useEffect(() => {
    const controller = new AbortController();
    setSnapshot(previous => ({ key, rows: previous?.key === key ? previous.rows : null, loading: true, error: "" }));
    async function read() {
      try {
        const rows = [];
        const pageSize = 1000;
        for (let offset = 0; ; offset += pageSize) {
          const query = new URLSearchParams({ select: "*,job_headers(route_seq)", order: "scheduled_date.asc,id.asc", limit: String(pageSize), offset: String(offset) });
          query.append("scheduled_date", `gte.${from}`);
          query.append("scheduled_date", `lte.${to}`);
          const response = await fetch(`${SUPABASE_URL}/rest/v1/jobs?${query}`, {
            headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` }, signal: controller.signal,
          });
          if (!response.ok) throw new Error(response.status === 401 ? "Your session has expired. Sign in again to refresh the schedule." : "The schedule could not be loaded. Please try again.");
          const page = await response.json();
          if (!Array.isArray(page)) throw new Error("The schedule returned an unexpected response. Please try again.");
          rows.push(...page);
          if (page.length < pageSize) break;
        }
        if (!controller.signal.aborted) setSnapshot({ key, rows, loading: false, error: "" });
      } catch (error) {
        if (!controller.signal.aborted) setSnapshot(previous => ({ key, rows: previous?.key === key ? previous.rows : null, loading: false, error: error.message || "Unable to refresh the schedule." }));
      }
    }
    read();
    return () => controller.abort();
  }, [from, to, key, revision, initialJobs]);

  const current = snapshot?.key === key ? snapshot : null;
  return { rows: current?.rows ?? seed, loading: current?.loading ?? true, error: current?.error || "", reload };
}

// ── Weather ──────────────────────────────────────────────────────────
// Source: the US National Weather Service, via the `weather-forecast` edge function.
//
// It used to call api.open-meteo.com directly from the browser. Open-Meteo's free tier
// is licensed for NON-COMMERCIAL use only, and this is a paid product for a business,
// so that call was out of terms. NWS is public domain, free, needs no key, and is
// explicitly fine commercially. It runs through an edge function because NWS asks
// callers to identify themselves with a User-Agent (a browser cannot set one) and
// because it is a two-hop API worth caching server-side.
//
// ⚠️ THE WINDOW SHRANK FROM 16 DAYS TO ABOUT 7. NWS does not forecast further out, and
//    a day past its horizon returns NOTHING rather than an extrapolation. That is
//    deliberate: "Outside forecast window" is honest, an invented number is not.
const forecastCache = new Map();
const NWS_HORIZON_DAYS = 7;

export function useScheduleWeather(date, clients, today) {
  const [forecast, setForecast] = useState({});
  const [loading, setLoading] = useState(false);
  const coordinates = useMemo(() => {
    const byZip = new Map();
    clients.filter(c => c.active && c.zip && Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)) && c.lat != null && c.lng != null)
      .forEach(c => { if (!byZip.has(c.zip)) byZip.set(c.zip, { zip: c.zip, lat: Number(c.lat), lng: Number(c.lng) }); });
    return [...byZip.values()];
  }, [clients]);
  const start = localDateStr(today);
  const endDate = civilDate(start); endDate.setDate(endDate.getDate() + NWS_HORIZON_DAYS);
  const inWindow = date >= start && date <= localDateStr(endDate);

  useEffect(() => {
    if (!inWindow || !coordinates.length) return;
    let alive = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    const cacheKey = coordinates.map(c => c.zip).sort().join(",") + "/" + start;
    const cached = forecastCache.get(cacheKey);
    if (cached && Date.now() - cached.time < 1800000) {
      setForecast(cached.byZip);
      return () => { clearTimeout(timeout); controller.abort(); };
    }
    setLoading(true);
    fetch(`${SUPABASE_URL}/functions/v1/weather-forecast`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
      body: JSON.stringify({ points: coordinates }),
      signal: controller.signal,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        clearTimeout(timeout);
        if (!alive) return;
        const byZip = d?.forecasts || {};
        forecastCache.set(cacheKey, { byZip, time: Date.now() });
        setForecast(byZip);
        setLoading(false);
      })
      .catch(() => { clearTimeout(timeout); if (alive) setLoading(false); });
    return () => { alive = false; clearTimeout(timeout); controller.abort(); };
  }, [coordinates, inWindow, start]);

  const reports = coordinates.map(({ zip }) => {
    const days = forecast[zip];
    if (!Array.isArray(days)) return null;
    const day = days.find(d => d.date === date);
    if (!day) return null;
    // UNITS, and the one that bites: Open-Meteo returned snowfall in CENTIMETRES and the
    // old threshold was `snow > 0.5`. NWS returns MILLIMETRES, so the same half-centimetre
    // of snow is 5 here. Precipitation (mm) and wind (km/h) are unchanged.
    const rain = day.precip_mm, snow = day.snow_mm, wind = day.wind_kph;
    if (rain == null || snow == null || wind == null) return null;
    const label = snow > 5 ? "Snow expected"
      : rain > 5 ? "Heavy rain expected"
      : rain > 1 ? "Rain expected"
      : wind > 40 ? "Strong winds expected"
      : "No significant weather alerts";
    return { zip, label, alert: snow > 5 || rain > 1 || wind > 40 };
  }).filter(Boolean);
  const alerts = reports.filter(r => r.alert);
  return {
    loading: inWindow && loading,
    alert: !!alerts.length,
    label: !inWindow ? "Outside forecast window" : loading ? "Checking weather…" : !reports.length ? "Forecast unavailable" : alerts.length ? alerts[0].label : "No significant weather alerts",
    detail: alerts.length ? `${alerts.map(r => r.zip).join(", ")} · ${reports.length} of ${coordinates.length} ZIPs available` : reports.length ? `${reports.length} of ${coordinates.length} service ZIPs available` : "Weather is supplementary; visits remain scheduled.",
  };
}
